import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import MobileStepper from '@mui/material/MobileStepper';
import { alpha } from '@mui/material/styles';
import { withStyles } from '@mui/styles';
import { useState } from 'react';
import Iconify from '../components/iconify/component';
import { type IconifyProps } from '../components/iconify/types';
import { checkUndefined } from './Tools';

export type DialogBoxData = {
    abort?: () => void;
    autoFunc?: () => void;
    autoTimeout?: number;
    continueOnNegative?: boolean;
    error?: boolean;
    hideAbort?: boolean;
    icon?: IconifyProps;
    info?: boolean;
    message?: string | any;
    negativeAnswer?: () => void;
    negativeAnswerIcon?: any;
    negativeAnswerText?: string;
    noElevation?: boolean;
    noIcon?: boolean;
    positiveAnswer?: () => void;
    positiveAnswerIcon?: any;
    positiveAnswerText?: string;
    secondAnswer?: () => void;
    secondAnswerIcon?: any;
    secondAnswerText?: string;
    success?: boolean;
    title?: string;
    warning?: boolean;
    white?: boolean;
};

type DialogBoxProps = {
    classes?: any;
    dialogData?: DialogBoxData | DialogBoxData[];
};

const styles = (theme) => ({
    root: {
        background: 'linear-gradient(60deg, ' + theme?.palette?.primary?.lighter + ', ' + theme?.palette?.primary?.darker + ')',
        color: '#fff',
        marginBottom: 10,
    },
    shadowRoot: {
        boxShadow:
            '0px 11px 15px -7px ' +
            alpha(theme?.palette?.primary?.dark, 0.1) +
            ', 0px 24px 38px 3px ' +
            alpha(theme?.palette?.primary?.dark, 0.1) +
            ', 0px 9px 46px 8px ' +
            alpha(theme?.palette?.primary?.dark, 0.1) +
            '',
    },
    error: {
        background: 'linear-gradient(60deg, ' + theme?.palette?.error?.lighter + ', ' + theme?.palette?.error?.darker + ')',
        color: '#fff',
        marginBottom: 10,
    },
    shadowError: {
        boxShadow:
            '0px 11px 15px -7px ' +
            alpha(theme?.palette?.error?.dark, 0.1) +
            ', 0px 24px 38px 3px ' +
            alpha(theme?.palette?.error?.dark, 0.1) +
            ', 0px 9px 46px 8px ' +
            alpha(theme?.palette?.error?.dark, 0.1) +
            '',
    },
    warning: {
        background: 'linear-gradient(60deg, ' + theme?.palette?.warning?.lighter + ', ' + theme?.palette?.warning?.darker + ')',
        color: '#fff',
        marginBottom: 10,
    },
    shadowWarning: {
        boxShadow:
            '0px 11px 15px -7px ' +
            alpha(theme?.palette?.warning?.dark, 0.1) +
            ', 0px 24px 38px 3px ' +
            alpha(theme?.palette?.warning?.dark, 0.1) +
            ', 0px 9px 46px 8px ' +
            alpha(theme?.palette?.warning?.dark, 0.1) +
            '',
    },
    success: {
        background: 'linear-gradient(60deg, ' + theme?.palette?.success?.lighter + ', ' + theme?.palette?.success?.darker + ')',
        color: '#fff',
        marginBottom: 10,
    },
    shadowSuccess: {
        boxShadow:
            '0px 11px 15px -7px ' +
            alpha(theme?.palette?.success?.dark, 0.1) +
            ', 0px 24px 38px 3px ' +
            alpha(theme?.palette?.success?.dark, 0.1) +
            ', 0px 9px 46px 8px ' +
            alpha(theme?.palette?.success?.dark, 0.1) +
            '',
    },
    info: {
        background: 'linear-gradient(60deg, ' + theme?.palette?.info?.lighter + ', ' + theme?.palette?.info?.darker + ')',
        color: '#fff',
        marginBottom: 10,
    },
    shadowInfo: {
        boxShadow:
            '0px 11px 15px -7px ' +
            alpha(theme?.palette?.info?.dark, 0.1) +
            ', 0px 24px 38px 3px ' +
            alpha(theme?.palette?.info?.dark, 0.1) +
            ', 0px 9px 46px 8px ' +
            alpha(theme?.palette?.info?.dark, 0.1) +
            '',
    },
    white: {
        background: theme?.palette?.background.paper,
        color: '#fff',
        marginBottom: 10,
    },
    shadowWhite: {
        boxShadow: '0px 11px 15px -7px ' + alpha('#000', 0.1) + ', 0px 24px 38px 3px ' + alpha('#000', 0.1) + ', 0px 9px 46px 8px ' + alpha('#000', 0.1) + '',
    },
});

const DialogBox = (moduleProps: DialogBoxProps) => {
    const { classes, dialogData } = moduleProps;

    const [activeStep, setActiveStep] = useState<any>(0);
    const paperStyle = Array.isArray(dialogData)
        ? dialogData[activeStep].error
            ? classes.error
            : dialogData[activeStep].warning
              ? classes.warning
              : dialogData[activeStep].success
                ? classes.success
                : dialogData[activeStep].info
                  ? classes.info
                  : dialogData[activeStep].white
                    ? classes.white
                    : classes.root
        : dialogData.error
          ? classes.error
          : dialogData.warning
            ? classes.warning
            : dialogData.success
              ? classes.success
              : dialogData.info
                ? classes.info
                : dialogData.white
                  ? classes.white
                  : classes.root;
    const shadowStyle = Array.isArray(dialogData)
        ? dialogData[activeStep].error
            ? classes.shadowError
            : dialogData[activeStep].warning
              ? classes.shadowWarning
              : dialogData[activeStep].success
                ? classes.shadowSuccess
                : dialogData[activeStep].info
                  ? classes.shadowInfo
                  : dialogData[activeStep].white
                    ? classes.shadowWhite
                    : classes.shadowRoot
        : dialogData.error
          ? classes.shadowError
          : dialogData.warning
            ? classes.shadowWarning
            : dialogData.success
              ? classes.shadowSuccess
              : dialogData.info
                ? classes.shadowInfo
                : dialogData.white
                  ? classes.shadowWhite
                  : classes.shadowRoot;

    const negativeAnswer = () => {
        if (Array.isArray(dialogData)) {
            dialogData[activeStep].negativeAnswer();
            if (activeStep + 1 < dialogData.length) {
                if (dialogData[activeStep].continueOnNegative) {
                    setActiveStep(activeStep + 1);
                }
            }
        }
    };

    const positiveAnswer = () => {
        if (Array.isArray(dialogData)) {
            dialogData[activeStep].positiveAnswer();
            if (activeStep + 1 < dialogData.length) {
                setActiveStep(activeStep + 1);
            }
        }
    };

    const secondAnswer = () => {
        if (Array.isArray(dialogData)) {
            dialogData[activeStep].secondAnswer();
            if (activeStep + 1 < dialogData.length) {
                setActiveStep(activeStep + 1);
            }
        }
    };

    if (Array.isArray(dialogData)) {
        if (dialogData[activeStep].autoFunc && dialogData[activeStep].autoTimeout) {
            setTimeout(function () {
                if (dialogData[activeStep].autoFunc) {
                    dialogData[activeStep].autoFunc();
                }
            }, dialogData[activeStep].autoTimeout);
        }
    } else if (dialogData.autoFunc && dialogData.autoTimeout) {
        setTimeout(function () {
            if (dialogData.autoFunc) {
                dialogData.autoFunc();
            }
        }, dialogData.autoTimeout);
    }

    return (
        <Dialog
            open
            onClose={
                Array.isArray(dialogData)
                    ? dialogData[activeStep].abort
                        ? dialogData[activeStep].abort
                        : dialogData[activeStep].negativeAnswer
                          ? dialogData[activeStep].negativeAnswer
                          : undefined
                    : dialogData.abort
                      ? dialogData.abort
                      : dialogData.negativeAnswer
                        ? dialogData.negativeAnswer
                        : undefined
            }
            aria-labelledby="title"
            PaperProps={{
                classes: {
                    root: paperStyle,
                    elevation24: shadowStyle,
                },
            }}
        >
            {Array.isArray(dialogData) ? (
                <DialogTitle id="title" sx={{ color: dialogData[activeStep].white ? 'text.primary' : 'common.white' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        {dialogData[activeStep].title ? (
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                {dialogData[activeStep].icon || dialogData[activeStep].error || dialogData[activeStep].warning || dialogData[activeStep].success || dialogData[activeStep].info ? (
                                    <span style={{ marginRight: 8, lineHeight: '100%' }}>
                                        {!dialogData[activeStep].noIcon ? (
                                            <Iconify
                                                icon={
                                                    dialogData[activeStep].icon
                                                        ? dialogData[activeStep].icon
                                                        : dialogData[activeStep].error
                                                          ? 'mdi:alert-circle'
                                                          : dialogData[activeStep].warning
                                                            ? 'mdi:alert'
                                                            : dialogData[activeStep].success
                                                              ? 'mdi:check-circle'
                                                              : dialogData[activeStep].info
                                                                ? 'mdi:information'
                                                                : undefined
                                                }
                                            />
                                        ) : undefined}
                                    </span>
                                ) : undefined}
                                <span style={{ lineHeight: '100%' }}>{dialogData[activeStep].title}</span>
                            </div>
                        ) : (
                            <div />
                        )}
                        <MobileStepper position="static" backButton={undefined} nextButton={undefined} variant="dots" steps={dialogData.length} activeStep={activeStep} />
                    </div>
                </DialogTitle>
            ) : dialogData.title ? (
                <DialogTitle id="title" sx={{ color: dialogData.white ? 'text.primary' : 'common.white' }}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        {dialogData.icon || dialogData.error || dialogData.warning || dialogData.success || dialogData.info ? (
                            <span style={{ marginRight: 8, lineHeight: '100%' }}>
                                {!dialogData.noIcon ? (
                                    <Iconify
                                        icon={
                                            dialogData.icon
                                                ? dialogData.icon
                                                : dialogData.error
                                                  ? 'mdi:alert-circle'
                                                  : dialogData.warning
                                                    ? 'mdi:alert'
                                                    : dialogData.success
                                                      ? 'mdi:check-circle'
                                                      : dialogData.info
                                                        ? 'mdi:information'
                                                        : undefined
                                        }
                                    />
                                ) : undefined}
                            </span>
                        ) : undefined}
                        <span style={{ lineHeight: '100%' }}>{dialogData.title}</span>
                    </div>
                </DialogTitle>
            ) : undefined}
            {Array.isArray(dialogData) ? (
                dialogData[activeStep].message ? (
                    <DialogContent>{dialogData[activeStep].message}</DialogContent>
                ) : undefined
            ) : dialogData.message ? (
                <DialogContent>{dialogData.message}</DialogContent>
            ) : undefined}
            {Array.isArray(dialogData) ? (
                <DialogActions>
                    {dialogData[activeStep].abort && !dialogData[activeStep].hideAbort ? (
                        <Button onClick={dialogData[activeStep].abort} sx={{ color: dialogData[activeStep].white ? 'text.primary' : 'common.white' }}>
                            <div data-testid="abort" style={{ display: 'flex', alignItems: 'center' }}>
                                <span style={{ marginRight: 8, lineHeight: '100%' }}>
                                    <Iconify icon="mdi:close" />
                                </span>
                                <span style={{ lineHeight: '100%' }}>Abbrechen</span>
                            </div>
                        </Button>
                    ) : undefined}
                    {dialogData[activeStep].negativeAnswer ? (
                        <Button onClick={negativeAnswer} sx={{ color: dialogData[activeStep].white ? 'text.primary' : 'common.white' }}>
                            <div data-testid="no" style={{ display: 'flex', alignItems: 'center' }}>
                                <span style={{ marginRight: 8, lineHeight: '100%' }}>
                                    {!checkUndefined(dialogData[activeStep].negativeAnswerIcon) ? dialogData[activeStep].negativeAnswerIcon : <Iconify icon="mdi:close" />}
                                </span>
                                <span style={{ lineHeight: '100%' }}>{dialogData[activeStep].negativeAnswerText ? dialogData[activeStep].negativeAnswerText : 'Nein'}</span>
                            </div>
                        </Button>
                    ) : undefined}
                    {dialogData[activeStep].positiveAnswer ? (
                        <Button onClick={positiveAnswer} sx={{ color: dialogData[activeStep].white ? 'text.primary' : 'common.white' }}>
                            <div data-testid="yes" style={{ display: 'flex', alignItems: 'center' }}>
                                <span style={{ marginRight: 8, lineHeight: '100%' }}>
                                    {!checkUndefined(dialogData[activeStep].positiveAnswerIcon) ? dialogData[activeStep].positiveAnswerIcon : <Iconify icon="mdi:check" />}
                                </span>
                                <span style={{ lineHeight: '100%' }}>{dialogData[activeStep].positiveAnswerText ? dialogData[activeStep].positiveAnswerText : 'Ja'}</span>
                            </div>
                        </Button>
                    ) : undefined}
                    {dialogData[activeStep].secondAnswer ? (
                        <Button onClick={secondAnswer} sx={{ color: dialogData[activeStep].white ? 'text.primary' : 'common.white' }}>
                            <div data-testid="yes" style={{ display: 'flex', alignItems: 'center' }}>
                                <span style={{ marginRight: 8, lineHeight: '100%' }}>
                                    {!checkUndefined(dialogData[activeStep].secondAnswerIcon) ? dialogData[activeStep].secondAnswerIcon : <Iconify icon="mdi:check" />}
                                </span>
                                <span style={{ lineHeight: '100%' }}>{dialogData[activeStep].secondAnswerText ? dialogData[activeStep].secondAnswerText : 'Ja'}</span>
                            </div>
                        </Button>
                    ) : undefined}
                </DialogActions>
            ) : (
                <DialogActions>
                    {dialogData.abort && !dialogData.hideAbort ? (
                        <Button onClick={dialogData.abort} sx={{ color: dialogData.white ? 'text.primary' : 'common.white' }}>
                            <div data-testid="abort" style={{ display: 'flex', alignItems: 'center' }}>
                                <span style={{ marginRight: 8, lineHeight: '100%' }}>
                                    <Iconify icon="mdi:close" />
                                </span>
                                <span style={{ lineHeight: '100%' }}>Abbrechen</span>
                            </div>
                        </Button>
                    ) : undefined}
                    {dialogData.negativeAnswer ? (
                        <Button onClick={dialogData.negativeAnswer} sx={{ color: dialogData.white ? 'text.primary' : 'common.white' }}>
                            <div data-testid="no" style={{ display: 'flex', alignItems: 'center' }}>
                                <span style={{ marginRight: 8, lineHeight: '100%' }}>
                                    {!checkUndefined(dialogData.negativeAnswerIcon) ? dialogData.negativeAnswerIcon : <Iconify icon="mdi:close" />}
                                </span>
                                <span style={{ lineHeight: '100%' }}>{dialogData.negativeAnswerText ? dialogData.negativeAnswerText : 'Nein'}</span>
                            </div>
                        </Button>
                    ) : undefined}
                    {dialogData.positiveAnswer ? (
                        <Button onClick={dialogData.positiveAnswer} sx={{ color: dialogData.white ? 'text.primary' : 'common.white' }}>
                            <div data-testid="yes" style={{ display: 'flex', alignItems: 'center' }}>
                                <span style={{ marginRight: 8, lineHeight: '100%' }}>
                                    {!checkUndefined(dialogData.positiveAnswerIcon) ? dialogData.positiveAnswerIcon : <Iconify icon="mdi:check" />}
                                </span>
                                <span style={{ lineHeight: '100%' }}>{dialogData.positiveAnswerText ? dialogData.positiveAnswerText : 'Ja'}</span>
                            </div>
                        </Button>
                    ) : undefined}
                    {dialogData.secondAnswer ? (
                        <Button onClick={dialogData.secondAnswer} sx={{ color: dialogData.white ? 'text.primary' : 'common.white' }}>
                            <div data-testid="yes" style={{ display: 'flex', alignItems: 'center' }}>
                                <span style={{ marginRight: 8, lineHeight: '100%' }}>{!checkUndefined(dialogData.secondAnswerIcon) ? dialogData.secondAnswerIcon : <Iconify icon="mdi:check" />}</span>
                                <span style={{ lineHeight: '100%' }}>{dialogData.secondAnswerText ? dialogData.secondAnswerText : 'Ja'}</span>
                            </div>
                        </Button>
                    ) : undefined}
                </DialogActions>
            )}
        </Dialog>
    );
};

export default withStyles(styles)(DialogBox);
